import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Table } from "reactstrap";
import StatusBadge from "components/common/StatusBadge";
import { DateOnly } from "components/common/Date";
import { formatCurrency } from "utils/currency";
import { getClientAdvances } from "features/ClientAdvances/services/ClientAdvanceService";
import {
  ClientAdvanceSourceType,
  ClientAdvanceStatus,
  clientAdvanceStatusMap,
} from "features/ClientAdvances/constants/client-advance.constants";
import type { ClientAdvance } from "features/ClientAdvances/types/client-advance.types";

type LinkedClientAdvancesTableProps = {
  sourceType: ClientAdvanceSourceType;
  sourceId: number | string | null | undefined;
  /** Excluded from the list — e.g. the advance currently being created/edited. */
  excludeAdvanceId?: number | null;
  heading?: string;
  className?: string;
  /** Fires whenever the linked advances (re)load — lets the caller react to e.g. outstanding DRAFT advances. */
  onLoaded?: (advances: ClientAdvance[]) => void;
};

/** Read-only list of client advances raised against a given work order / petty work / AMC. */
export default function LinkedClientAdvancesTable({
  sourceType,
  sourceId,
  excludeAdvanceId,
  heading = "Linked advances",
  className,
  onLoaded,
}: LinkedClientAdvancesTableProps) {
  const [advances, setAdvances] = useState<ClientAdvance[]>([]);

  useEffect(() => {
    const id = Number(sourceId);
    if (!Number.isFinite(id) || id <= 0) {
      setAdvances([]);
      onLoaded?.([]);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const response = await getClientAdvances({
          sourceType,
          sourceId: id,
          limit: 100,
        });
        if (cancelled) return;
        const rows = response.data?.rows ?? [];
        const filtered =
          excludeAdvanceId != null
            ? rows.filter((r) => r.id !== excludeAdvanceId)
            : rows;
        setAdvances(filtered);
        onLoaded?.(filtered);
      } catch (err) {
        if (!cancelled) {
          console.warn(err);
          setAdvances([]);
          onLoaded?.([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceType, sourceId, excludeAdvanceId]);

  if (advances.length === 0) return null;

  const total = advances.reduce(
    (sum, a) =>
      a.status !== ClientAdvanceStatus.CANCELLED ? sum + (a.amount ?? 0) : sum,
    0,
  );

  return (
    <div className={className}>
      <p className="text-muted small mb-2">{heading}</p>
      <div className="table-responsive border rounded">
        <Table className="table-nowrap table-sm align-middle mb-0" size="sm">
          <thead className="table-light">
            <tr>
              <th>Code</th>
              <th>Status</th>
              <th className="text-end">Amount</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {advances.map((adv) => (
              <tr key={adv.id}>
                <td>
                  <Link
                    to={`/finance/client-advances/${adv.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="link fw-medium"
                  >
                    {adv.code}
                  </Link>
                </td>
                <td>
                  <StatusBadge value={adv.status} map={clientAdvanceStatusMap} />
                </td>
                <td className="text-end tabular-nums">
                  {formatCurrency(adv.amount)}
                </td>
                <td className="text-muted">
                  <DateOnly date={adv.created_at} />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th colSpan={2} className="text-end">
                Total (excl. cancelled)
              </th>
              <th className="text-end tabular-nums">{formatCurrency(total)}</th>
              <th />
            </tr>
          </tfoot>
        </Table>
      </div>
    </div>
  );
}
